import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createVideoFromSequence } from '@/utils/autoVideoCreation';
import { incrementalSyncService } from '@/lib/search/typesense/incrementalSyncService';
import { SmartUploadService } from '@/lib/uploadService';
import { 
  SequenceFormData, 
  sequenceSchema, 
  UploadedFile, 
  WizardStep,
  WIZARD_STEPS 
} from '../types';
import { SpotifyTrack } from '@/services/spotifyService';
import { LayoutPhotoFile } from '@/components/sequences/upload/LayoutPhotoUpload';
import { layoutPhotoService } from '@/services/layoutPhotoService';

export const useUploadWizard = (
  onSuccess?: (sequenceId: string, sequenceData: any) => void,
  onCancel?: () => void
) => {
  const { user, validateSession } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [selectedSpotifyTrack, setSelectedSpotifyTrack] = useState<SpotifyTrack | null>(null);
  const [layoutPhotos, setLayoutPhotos] = useState<LayoutPhotoFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftSequenceId, setDraftSequenceId] = useState<string | null>(null);

  const form = useForm<SequenceFormData>({
    resolver: zodResolver(sequenceSchema),
    defaultValues: {
      is_free: false,
      price: 0,
      software_type: 'xLights',
      holiday_type: 'Christmas',
      lor_metadata: {
        controller_type: undefined,
        channel_count_ac: undefined,
        software_version: undefined,
        prop_types: [],
        license_level_required: undefined,
        sequence_type_tags: [],
        import_notes: '',
      },
    },
  });

  const getCurrentStepId = (): WizardStep => {
    return WIZARD_STEPS[currentStep].id;
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < WIZARD_STEPS.length) {
      setCurrentStep(step);
    }
  };

  const goNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadFileToStorage = useCallback(async (file: File) => {
    if (!user) {
      toast.error('You must be logged in to upload files');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Client-side validation for supported file types
      const allowedExtensions = ['zip', 'fseq', 'lms', 'seq', 'xml', 'piz'];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        toast.error('Unsupported file type. Allowed: .zip, .fseq, .lms, .seq, .xml');
        setUploadedFile(null);
        return;
      }

      // Note: Auto-detection of Light-O-Rama files is now handled in handleFileSelect
      // to ensure it happens immediately when a file is selected, regardless of upload success

      // First, validate that we have a valid session and refresh if needed
      const isSessionValid = await validateSession();
      
      if (!isSessionValid) {
        console.error('Session validation failed');
        toast.error('Your session has expired. Please log in again.');
        setUploadedFile(null);
        return;
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Session retrieval failed:', sessionError);
        toast.error('Authentication error. Please log in again.');
        setUploadedFile(null);
        return;
      }

		// Env for resumable uploads (avoid referencing `process` in the browser)
		const SUPABASE_URL =
			(typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_URL) ||
			(typeof window !== 'undefined' && ((window as any).VITE_SUPABASE_URL || (window as any).NEXT_PUBLIC_SUPABASE_URL)) ||
			undefined;
		const SUPABASE_ANON_KEY =
			(typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_ANON_KEY) ||
			(typeof window !== 'undefined' && ((window as any).VITE_SUPABASE_ANON_KEY || (window as any).NEXT_PUBLIC_SUPABASE_ANON_KEY)) ||
			undefined;

		// Extract project ID from Supabase URL for direct storage hostname
		const getProjectIdFromUrl = (url: string): string | null => {
			const match = url.match(/https:\/\/([^\.]+)\.supabase\.co/);
			return match ? match[1] : null;
		};

		async function uploadViaResumable(): Promise<boolean> {
			try {
				if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
					console.error('Missing Supabase configuration for resumable upload');
					return false;
				}
				
				const { data: sess } = await supabase.auth.getSession();
				const accessToken = sess.session?.access_token;
				if (!accessToken) {
					console.error('No access token available for resumable upload');
					return false;
				}

				// Extract project ID and use direct storage hostname for better performance
				const projectId = getProjectIdFromUrl(SUPABASE_URL);
				if (!projectId) {
					console.error('Could not extract project ID from Supabase URL');
					return false;
				}

				const tus = await import('tus-js-client');
				
				// Use direct storage hostname per Supabase documentation
				const endpoint = `https://${projectId}.storage.supabase.co/storage/v1/upload/resumable`;
				
				console.log('Starting resumable upload with endpoint:', endpoint);
				console.log('Upload filename:', fileName);

				const resumeKey = `tus:${fileName}`;
				const previousUrl = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(resumeKey) || undefined : undefined;

				await new Promise<void>((resolve, reject) => {
					const upload = new tus.Upload(file, {
						endpoint,
						chunkSize: 6 * 1024 * 1024, // Use recommended 6MB chunk size
						retryDelays: [0, 3000, 5000, 10000, 20000], // Per Supabase docs
						uploadDataDuringCreation: true, // Send metadata with file chunks
						removeFingerprintOnSuccess: true, // Important for re-uploading same file
						headers: {
							authorization: `Bearer ${accessToken}`, // User session access token
							apikey: SUPABASE_ANON_KEY, // Required API key header
						},
						metadata: {
							bucketName: 'sequences',
							objectName: fileName,
							contentType: contentType,
							cacheControl: '3600',
						},
						onError: (error) => {
							console.error('TUS Upload error:', error);
							if (typeof sessionStorage !== 'undefined') {
								sessionStorage.removeItem(resumeKey);
							}
							reject(error);
						},
						onProgress: (bytesUploaded, bytesTotal) => {
							const percentage = Math.min(99, Math.floor((bytesUploaded / bytesTotal) * 100));
							setUploadProgress(percentage);
						},
						onSuccess: () => {
							console.log('TUS Upload completed successfully');
							if (typeof sessionStorage !== 'undefined') {
								sessionStorage.removeItem(resumeKey);
							}
							setUploadProgress(100);
							resolve();
						}
					});

					// Store upload URL for resume capability
					if (previousUrl) {
						upload.url = previousUrl;
					}

					upload.findPreviousUploads()
						.then((previousUploads) => {
							if (previousUploads.length) {
								console.log('Resuming previous upload');
								upload.resumeFromPreviousUpload(previousUploads[0]);
							}
							upload.start();
							
							// Save the upload URL for potential resume
							if (typeof sessionStorage !== 'undefined' && upload.url) {
								sessionStorage.setItem(resumeKey, upload.url);
							}
						})
						.catch((error) => {
							console.error('Error finding previous uploads:', error);
							reject(error);
						});
				});
				
				return true;
			} catch (error) {
				console.error('Resumable upload failed:', error);
				return false;
			}
		}

		// Generate unique filename with user ID prefix to match RLS policies
		const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
		let fileName = `${session.user.id}/${Date.now()}-${safeFileName}`;

		// Determine content type explicitly with better detection
		let contentType = file.type;
		if (!contentType || contentType === 'application/octet-stream') {
			const ext = safeFileName.split('.').pop()?.toLowerCase();
			if (ext === 'zip') contentType = 'application/zip';
			else if (ext === 'xml') contentType = 'text/xml';
			else if (ext === 'lms') contentType = 'application/x-lms';
			else if (ext === 'fseq') contentType = 'application/x-fseq';
			else if (ext === 'seq') contentType = 'application/x-seq';
			else contentType = 'application/octet-stream';
		}

		console.log('Attempting file upload:', { fileName, fileSize: file.size, fileType: file.type, explicitContentType: contentType, userId: session.user.id, bucket: 'sequences' });
		console.log('Starting upload with path:', fileName);
		console.log('Upload details:', { fileName, fileSize: file.size, contentType, userId: session.user.id, bucket: 'sequences' });

		setUploadProgress(1);
		const progressInterval = setInterval(() => setUploadProgress((p) => Math.min(p + 1, 90)), 350);

		try {
			// Choose single path per policy to avoid racing/early checks
			const ext = safeFileName.split('.').pop()?.toLowerCase();
			const preferSigned = ext === 'lms' || file.size <= 6 * 1024 * 1024;

			if (!preferSigned) {
				// Large files: try resumable first
				console.log('Attempting resumable upload for large file...');
				const ok = await uploadViaResumable();
				if (ok) {
					clearInterval(progressInterval);
					setUploadProgress(100);
					// Mark file as uploaded for later steps
					setUploadedFile(prev => {
						if (prev) {
							return Object.assign(prev, {
								uploadedPath: fileName,
								uploadedSize: file.size
							});
						}
						return null;
					});
					console.log('✅ Resumable upload complete:', { path: fileName });
					toast.success('File uploaded successfully via resumable upload!');
					return;
				}
				// Backoff before fallback
				console.log('Resumable upload failed, falling back to signed upload after delay...');
				await new Promise((r) => setTimeout(r, 3000)); // Reduced delay
			}

			// Signed upload (default for small/.lms and fallback for large)
			console.log('Using signed upload method...');
			const { data: sessForSigned } = await supabase.auth.getSession();
			const accessToken = sessForSigned.session?.access_token;
			
			if (!accessToken) {
				throw new Error('No access token available for signed upload');
			}

			console.log('Requesting signed upload URL...');
			const signedRes = await fetch('/api/storage/signed-upload', {
				method: 'POST',
				headers: { 
					'Content-Type': 'application/json', 
					Authorization: `Bearer ${accessToken}` 
				},
				body: JSON.stringify({ fileName, userId: session.user.id })
			});

			if (!signedRes.ok) {
				let details = '';
				try { details = await signedRes.text(); } catch {}
				console.error('Signed upload URL request failed:', { status: signedRes.status, details });
				throw new Error(`Signed upload URL request failed: ${signedRes.status}`);
			}

			const { path, token } = await signedRes.json();
			console.log('Got signed upload URL, uploading file...', { path });

			const { error } = await supabase.storage
				.from('sequences')
				.uploadToSignedUrl(path, token, file, { 
					contentType,
					cacheControl: '3600'
				});

			if (error) {
				console.error('uploadToSignedUrl error:', error);
				throw new Error(`Upload to signed URL failed: ${error.message}`);
			}

			clearInterval(progressInterval);
			setUploadProgress(100);
			
			// Consolidate state update logic
			setUploadedFile(prev => {
				if (prev) {
					const updatedFile = Object.assign(prev, {
						uploadedPath: path,
						uploadedSize: file.size
					});
					console.log('✅ Signed upload complete. Updated file state:', {
						name: updatedFile.name,
						uploadedPath: updatedFile.uploadedPath,
						uploadedSize: updatedFile.uploadedSize
					});
					return updatedFile;
				}
				console.warn('No previous file state found during signed upload completion');
				return null;
			});
			
			console.log('✅ Signed upload complete:', { path });
			toast.success('File uploaded successfully!');
			return;
 
		} catch (err) {
			clearInterval(progressInterval);
			console.error('❌ File upload error:', err);
			console.error('Upload context:', { 
				fileName, 
				fileSize: file.size, 
				contentType, 
				userId: session.user.id, 
				bucket: 'sequences',
				preferSigned: ext === 'lms' || file.size <= 6 * 1024 * 1024,
				uploadMethod: !preferSigned ? 'resumable->signed' : 'signed'
			});
			setUploadProgress(0);
			
			// Improved error handling with specific messages
			if (err instanceof Error) {
				if (err.message?.includes('network') || err.message?.includes('fetch')) {
					toast.error('Network error. Please check your connection and try again.');
				} else if (err.message?.includes('401') || err.message?.includes('authentication')) {
					toast.error('Authentication error. Please refresh the page and try again.');
				} else if (err.message?.includes('413') || err.message?.includes('too large')) {
					toast.error('File is too large. Please try a smaller file.');
				} else if (err.message?.includes('400')) {
					toast.error('Invalid file or upload request. Please try a different file.');
				} else {
					toast.error(`Upload failed: ${err.message}`);
				}
			} else {
				toast.error('Failed to upload file. Please try again.');
			}
			
			throw err;
		}

      // Pre-create a draft sequence row to mirror admin flow (so finalize can UPDATE)
      let draftId: string | null = null;
      try {
        const draftTitle = (file.name || 'Draft Sequence').replace(/\.[^/.]+$/, '') || 'Draft Sequence';
        const { data: draft, error: draftErr } = await supabase
          .from('sequences')
          .insert({ user_id: session.user.id, name: draftTitle } as any)
          .select('id')
          .single();
        if (draftErr) {
          console.warn('Failed to create draft sequence (will continue without draftId):', draftErr);
        } else if (draft && (draft as any).id) {
          draftId = (draft as any).id as string;
          setDraftSequenceId((draft as any).id as string);
        }
      } catch (e) {
        console.warn('Failed to create draft sequence (continuing without it):', e);
      }

      // This code should not be reached anymore due to the explicit returns above
      console.warn('Reached unreachable upload completion code - this should not happen');
    } catch (error) {
      console.error('File upload error:', error);
      
      // More specific error handling
      if (error instanceof Error) {
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
          toast.error('Network error. Please check your connection and try again.');
        } else if (!error.message?.includes('Upload failed:')) {
          toast.error('Failed to upload file. Please try again.');
        }
      } else {
        toast.error('Failed to upload file. Please try again.');
      }
      
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  }, [user]);

  const handleFileSelect = useCallback(async (file: File) => {
    // Create an UploadedFile object with the File properties
    const uploadedFileObj: UploadedFile = {
      ...file,
      uploadedPath: undefined,
      uploadedSize: undefined,
      // Preserve the original File properties explicitly
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    } as UploadedFile;
    
    setUploadedFile(uploadedFileObj);
    
    // Auto-populate title from filename (remove extension)
    const titleFromFile = file.name.replace(/\.[^/.]+$/, '');
    form.setValue('title', titleFromFile);
    
    // Auto-detect Light-O-Rama file extensions and set software type immediately
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const lorExtensions = ['lms', 'loredit', 'las'];
    if (lorExtensions.includes(ext)) {
      form.setValue('software_type', 'Light-O-Rama');
      toast.success('Light-O-Rama file detected! LOR settings will be available.');
    }
    
    // Start file upload
    toast.message('File selected. Starting upload...');
    try {
      await uploadFileToStorage(file);
      
      // Auto-advance to next step after successful upload
      setTimeout(() => {
        toast.success('Moving to next step...');
        goNext();
      }, 1500); // Small delay to show success message
    } catch (error) {
      // Upload failed, don't auto-advance
      console.error('Upload failed:', error);
    }
  }, [form, uploadFileToStorage, goNext]);

  const handleFileRemove = useCallback(() => {
    setUploadedFile(null);
    setUploadProgress(0);
  }, []);

  const handleSpotifyTrackSelect = useCallback((track: SpotifyTrack | null) => {
    setSelectedSpotifyTrack(track);
  }, []);

  // Artist management functions (similar to admin sequences)
  const normalizeArtistName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[-\s]+/g, ' ')
      .replace(/[^\w\s]/g, '')
      .trim();
  };

  const findArtistByName = async (name: string): Promise<any | null> => {
    try {
      const normalizedName = normalizeArtistName(name);
      
      // Search in database directly for most accurate results
      const { data, error } = await supabase
        .from('artists')
        .select('*')
        .ilike('name', name); // Case-insensitive search
      
      if (error) throw error;
      
      // Find exact match using normalization
      const exactMatch = data?.find(artist => 
        normalizeArtistName(artist.name) === normalizedName
      );
      
      return exactMatch || null;
    } catch (err) {
      console.error('Error finding artist by name:', err);
      return null;
    }
  };

  const createArtist = async (name: string, description?: string, imageUrl?: string) => {
    try {
      // First check if artist already exists
      const existingArtist = await findArtistByName(name);
      
      if (existingArtist) {
        console.log(`Artist "${name}" already exists, returning existing artist:`, existingArtist);
        return existingArtist;
      }

      // Create new artist
      const { data, error } = await supabase
        .from('artists')
        .insert([{
          name,
          description,
          image_url: imageUrl
        }])
        .select()
        .single();

      if (error) {
        // Handle duplicate key error
        if (error.code === '23505' || error.message?.includes('duplicate key')) {
          console.log(`Duplicate artist detected for "${name}", fetching existing...`);
          const existingArtist = await findArtistByName(name);
          if (existingArtist) {
            return existingArtist;
          }
        }
        throw error;
      }
      
      console.log(`Created new artist: "${name}"`, data);
      return data;
    } catch (err) {
      console.error('Error creating artist:', err);
      throw err;
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error('You must be logged in to submit a sequence');
      return;
    }

    if (!uploadedFile?.uploadedPath) {
      toast.error('No file uploaded');
      return;
    }

    const formData = form.getValues();
    
    // Validate LOR metadata if software type is Light-O-Rama
    if (formData.software_type === 'Light-O-Rama') {
      if (!formData.lor_metadata) {
        toast.error('Light-O-Rama metadata is required when software type is Light-O-Rama');
        return;
      }
      
      // Only validate controller type as required
      const lorMetadata = formData.lor_metadata;
      if (!lorMetadata.controller_type) {
        toast.error('Controller type is required for Light-O-Rama sequences');
        return;
      }
    }
    
    setIsSubmitting(true);

    try {
      // Handle artist creation/lookup first
      let artistId: string | null = null;
      if (formData.artist && formData.artist.trim()) {
        console.log('Creating/finding artist:', formData.artist);
        const artist = await createArtist(formData.artist.trim());
        artistId = artist.id;
        console.log('Artist resolved to ID:', artistId);
      }
      // Validate sequence data before insert/update
      const validHolidayTypes = ['Christmas', 'Halloween', 'FourthOfJuly', 'Thanksgiving', 'NewYear', 'Easter', 'Hanukkah', 'Diwali', 'ValentinesDay', 'StPatricksDay', 'IndependenceDay', 'MemorialDay', 'LaborDay', 'VeteransDay'];
      const genres = Array.isArray(formData.genres) ? formData.genres : [];
      
      // Validate genres array length
      if (genres.length > 6) {
        throw new Error('Maximum of 6 genres allowed');
      }

      // Validate holiday type
      if (formData.holiday_type && !validHolidayTypes.includes(formData.holiday_type)) {
        throw new Error(`Invalid holiday_type: ${formData.holiday_type}. Must be one of: ${validHolidayTypes.join(', ')}`);
      }

      // Validate primary and secondary genres are in genres array
      if (formData.primary_genre && !genres.includes(formData.primary_genre)) {
        throw new Error(`primary_genre "${formData.primary_genre}" must be included in the genres array`);
      }

      if (formData.secondary_genre && !genres.includes(formData.secondary_genre)) {
        throw new Error(`secondary_genre "${formData.secondary_genre}" must be included in the genres array`);
      }

      console.log('✅ Sequence validation passed');

      // If we created a draft earlier, update it; else insert new
      let sequence: any = null;
      if (draftSequenceId) {
        console.log('📝 Updating existing draft sequence...');
        const { data, error: updateErr } = await supabase
          .from('sequences')
          .update({
            name: formData.title,
            title: formData.title,
            description: formData.description,
            software_type: formData.software_type,
            holiday_type: formData.holiday_type,
            price: formData.is_free ? 0 : formData.price,
            is_free: formData.is_free,
            storage_path: uploadedFile.uploadedPath,
            original_filename: uploadedFile.name,
            content_type: uploadedFile.type || 'application/octet-stream',
            file_size: uploadedFile.uploadedSize || uploadedFile.size,
            artist_id: artistId,
            spotify_track_id: selectedSpotifyTrack?.id,
            video_url: formData.video_url,
            album: formData.album,
            album_cover_url: formData.album_cover_url,
            duration: formData.duration,
            spotify_preview_url: selectedSpotifyTrack?.preview_url,
            spotify_explicit: selectedSpotifyTrack?.explicit,
            spotify_duration_ms: selectedSpotifyTrack?.duration_ms,
            spotify_popularity: selectedSpotifyTrack?.popularity,
            primary_genre: formData.primary_genre || null,
            secondary_genre: formData.secondary_genre || null,
            genres: genres,
            genres_overridden: !!formData.genres_overridden
          } as any)
          .eq('id', draftSequenceId as any)
          .eq('user_id', user.id as any)
          .select()
          .single();
        if (updateErr) throw updateErr;
        sequence = data as any;
      } else {
        console.log('🆕 Inserting new sequence...');

        const { data, error: insertErr } = await supabase
          .from('sequences')
          .insert({
            user_id: user.id,
            name: formData.title,
            title: formData.title,
            description: formData.description,
            software_type: formData.software_type,
            holiday_type: formData.holiday_type,
            price: formData.is_free ? 0 : formData.price,
            is_free: formData.is_free,
            storage_path: uploadedFile.uploadedPath,
            original_filename: uploadedFile.name,
            content_type: uploadedFile.type || 'application/octet-stream',
            file_size: uploadedFile.uploadedSize || uploadedFile.size,
            artist_id: artistId,
            spotify_track_id: selectedSpotifyTrack?.id,
            video_url: formData.video_url,
            album: formData.album,
            album_cover_url: formData.album_cover_url,
            duration: formData.duration,
            spotify_preview_url: selectedSpotifyTrack?.preview_url,
            spotify_explicit: selectedSpotifyTrack?.explicit,
            spotify_duration_ms: selectedSpotifyTrack?.duration_ms,
            spotify_popularity: selectedSpotifyTrack?.popularity,
            primary_genre: formData.primary_genre || null,
            secondary_genre: formData.secondary_genre || null,
            genres: genres,
            genres_overridden: !!formData.genres_overridden
          } as any)
          .select()
          .single();
        if (insertErr) throw insertErr;
        sequence = data as any;
      }

      // sequenceError variable was undefined; rely on thrown errors above

      // Handle LOR metadata if software type is Light-O-Rama
      if (formData.software_type === 'Light-O-Rama' && formData.lor_metadata) {
        try {
          // Validate enum values
          const validControllerTypes = ['AC_ONLY', 'RGB', 'MIXED'];
          const validSoftwareVersions = ['S4', 'S5', 'S6'];
          const validLicenseLevels = ['BASIC', 'STANDARD', 'ADVANCED', 'PRO'];

          if (!validControllerTypes.includes(formData.lor_metadata.controller_type)) {
            throw new Error(`Invalid controller_type: ${formData.lor_metadata.controller_type}. Must be one of: ${validControllerTypes.join(', ')}`);
          }

          if (!validSoftwareVersions.includes(formData.lor_metadata.software_version)) {
            throw new Error(`Invalid software_version: ${formData.lor_metadata.software_version}. Must be one of: ${validSoftwareVersions.join(', ')}`);
          }

          if (!validLicenseLevels.includes(formData.lor_metadata.license_level_required)) {
            throw new Error(`Invalid license_level_required: ${formData.lor_metadata.license_level_required}. Must be one of: ${validLicenseLevels.join(', ')}`);
          }

          const { error: lorError } = await supabase
            .from('sequence_lor_meta')
            .insert({
              sequence_id: (sequence as any).id,
              controller_type: formData.lor_metadata.controller_type,
              channel_count_ac: formData.lor_metadata.channel_count_ac,
              software_version: formData.lor_metadata.software_version,
              prop_types: formData.lor_metadata.prop_types || [],
              license_level_required: formData.lor_metadata.license_level_required,
              sequence_type_tags: formData.lor_metadata.sequence_type_tags || [],
              import_notes: formData.lor_metadata.import_notes || null
            } as any);

          if (lorError) {
            console.error('Failed to create LOR metadata:', lorError);
            // Don't fail the sequence creation if LOR metadata creation fails
            // The user can still use the sequence, just without LOR-specific metadata
          } else {
            console.log('✅ LOR metadata created successfully');
          }
        } catch (error) {
          console.warn('Error creating LOR metadata:', error);
          // Don't fail the sequence creation if LOR metadata creation fails
        }
      }

      // If video URL is provided, automatically create a video entry
      if (formData.video_url && formData.video_url.trim()) {
        try {
          const videoResult = await createVideoFromSequence(
            sequence.id,
            formData.video_url,
            user.id,
            {
              title: formData.title,
              name: formData.title,
              artist: formData.artist,
              album: formData.album,
              spotify_track_id: selectedSpotifyTrack?.id,
              holiday_type: formData.holiday_type
            }
          );

          if (videoResult.success) {
            if (videoResult.wasExisting) {
              console.log('Video already existed, updated with sequence relationship');
            } else {
              console.log('New video created successfully:', videoResult.videoId);
            }
          } else {
            console.warn('Failed to create video automatically:', videoResult.error);
            // Don't fail the sequence creation if video creation fails
          }
        } catch (error) {
          console.warn('Error during automatic video creation:', error);
          // Don't fail the sequence creation if video creation fails
        }
      }

      // Upload layout photos if any
      if (layoutPhotos.length > 0) {
        try {
          console.log('📸 Uploading layout photos...');
          const photosToUpload = layoutPhotos.map(photo => ({
            file: photo.file,
            caption: photo.caption,
          }));

          await layoutPhotoService.uploadLayoutPhotos(sequence.id, photosToUpload);
          console.log('✅ Layout photos uploaded successfully');
        } catch (photoError) {
          console.warn('⚠️ Failed to upload layout photos:', photoError);
          // Don't fail the sequence creation if photo upload fails
          // The user can add photos later
        }
      }

      // Index the new sequence in Typesense
      try {
        await incrementalSyncService.syncSequence(sequence.id, 'insert');
        console.log('✅ Sequence indexed in Typesense successfully');
      } catch (syncError) {
        console.warn('⚠️ Failed to index sequence in Typesense:', syncError);
        // Don't fail the sequence creation if indexing fails
        // The database trigger will retry automatically
      }

      toast.success('Sequence submitted successfully!');
      
      if (onSuccess && sequence) {
        onSuccess(sequence.id, {
          title: formData.title,
          name: formData.title,
          artist: formData.artist,
          description: formData.description,
          ...sequence
        });
      }
    } catch (error) {
      console.error('❌ Detailed submission error:', {
        error,
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        formData: {
          title: formData.title,
          software_type: formData.software_type,
          holiday_type: formData.holiday_type,
          genresLength: Array.isArray(formData.genres) ? formData.genres.length : 0,
          hasLorMetadata: !!formData.lor_metadata,
          userId: user?.id
        },
        uploadedFile: uploadedFile ? {
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
          uploadedPath: uploadedFile.uploadedPath
        } : null
      });

      // Show user-friendly error messages based on error type
      if (error instanceof Error) {
        if (error.message.includes('genres')) {
          toast.error(`Genre validation error: ${error.message}`);
        } else if (error.message.includes('holiday_type')) {
          toast.error(`Holiday type validation error: ${error.message}`);
        } else if (error.message.includes('controller_type') || error.message.includes('software_version') || error.message.includes('license_level_required')) {
          toast.error(`LOR metadata validation error: ${error.message}`);
        } else if (error.message.includes('foreign key constraint') || error.message.includes('violates')) {
          toast.error('Database constraint violation. Please check your data and try again.');
        } else {
          toast.error(`Upload failed: ${error.message}`);
        }
      } else {
        toast.error('Failed to submit sequence. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // State
    currentStep,
    uploadedFile,
    selectedSpotifyTrack,
    layoutPhotos,
    uploadProgress,
    isUploading,
    isSubmitting,
    form,
    
    // Navigation
    getCurrentStepId,
    goToStep,
    goNext,
    goBack,
    
    // Actions
    handleFileSelect,
    handleFileRemove,
    handleSpotifyTrackSelect,
    handleLayoutPhotosChange: setLayoutPhotos,
    handleSubmit,
    onCancel,
    
    // Computed
    totalSteps: WIZARD_STEPS.length,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === WIZARD_STEPS.length - 1,
    currentStepInfo: WIZARD_STEPS[currentStep],
  };
};